import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  activateUser,
  deactivateUser,
  generateProvisionalPassword,
  getUserAuthDebug,
  getUserDocumentSignedUrl,
  getUserPhotoUrl,
  getClientDebug,
  getProfile,
  mergeUserPhotoIntoScope,
  resetPasswordForUser,
  softDeleteUser,
  updateProfile,
  uploadUserDocument,
  uploadUserPhoto,
  userOrderStats,
  type UserListResult,
} from '@/api/users'
import { PasswordDialog } from '@/components/users/PasswordDialog'
import { listCompanies } from '@/api/companies'
import { listOrders } from '@/api/orders'
import { emptyTableCellClassName } from '@/constants/ui'
import { Breadcrumbs } from '@/components/layout/Breadcrumbs'
import { Button } from '@/components/ui/Button'
import { PillToggle } from '@/components/ui/PillToggle'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { DetailHeaderSkeleton, SectionSkeleton } from '@/components/ui/Skeleton'
import { formatMoscowDateTime } from '@/utils/format'
import type { Profile } from '@/types/user'
import type { OrderStatus, UserRole } from '@/types/enums'
import { statusLabelRu } from '@/utils/status-transitions'
import { digitsOnly, formatPhoneDisplay, formatPhoneInput } from '@/utils/phone'
import { AlertDialog, ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { DocumentUploader } from '@/components/ui/DocumentUploader'
import { useAuthStore } from '@/store/authStore'
import i18n from '@/utils/i18n'

function roleLabel(role: UserRole) {
  const m: Record<UserRole, string> = {
    admin: 'role.adminPlatform',
    dispatcher: 'role.dispatcher',
    manager: 'role.manager',
    inspector: 'role.inspector',
    customer_supervisor: 'role.customerSupervisor',
    contractor_supervisor: 'role.contractorSupervisor',
    partner_operator: 'role.partnerOperator',
    partner_viewer: 'role.partnerViewer',
  }
  const key = m[role]
  return key ? i18n.t(key) : role
}

const valueClassName =
  'font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-base)] text-[color:var(--stryzh-color-text-primary)]'

export function UserDetailPage() {
  const { t } = useTranslation()
  const { id = '' } = useParams()
  const nav = useNavigate()
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState<Partial<Profile>>({})
  const [photoFile, setPhotoFile] = useState<File | null>(null)
  const [photoViewerUrl, setPhotoViewerUrl] = useState<string | null>(null)
  const [confirmDeactivateOpen, setConfirmDeactivateOpen] = useState(false)
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [resetPwDraft, setResetPwDraft] = useState<string | null>(null)
  const actorRole = useAuthStore((s) => s.profile?.role)
  const canEditRole = actorRole === 'admin'

  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['profile', id],
    queryFn: () => getProfile(id),
    enabled: !!id,
    /**
     * Если мы пришли из `/users` — список уже в кэше. Берём оттуда
     * первый рендер, фоном догрузится свежий profile. Клик по строке
     * ощущается мгновенным даже при сетевой latency.
     */
    initialData: () => {
      if (!id) return undefined
      // Защищаемся `Array.isArray(v.items)` от случая, когда в кэше под
      // префиксом ['users', ...] окажется запрос с другой формой данных
      // (см. тот же фикс в OrderDetailPage). Иначе `v?.items.find` падает
      // при первом монтировании после createUser/navigate.
      const lists = qc.getQueriesData<UserListResult>({ queryKey: ['users'] })
      for (const [, v] of lists) {
        if (!v || !Array.isArray(v.items)) continue
        const hit = v.items.find((u) => u.id === id)
        if (hit) return hit
      }
      return undefined
    },
    initialDataUpdatedAt: () =>
      qc.getQueryState(['users'])?.dataUpdatedAt,
  })

  const { data: orders } = useQuery({
    queryKey: ['user-orders', id],
    queryFn: () => listOrders({ page: 1, pageSize: 100, userId: id }),
    enabled: !!id,
    select: (d) => d.items,
  })

  const { data: debug } = useQuery({
    queryKey: ['client-debug', id],
    queryFn: () => getClientDebug(id),
    enabled: !!id,
  })

  const { data: authDebug } = useQuery({
    queryKey: ['user-auth-debug', id],
    queryFn: () => getUserAuthDebug(id),
    enabled: !!id,
    staleTime: 0,
  })

  const { data: stats } = useQuery({
    queryKey: ['user-order-stats', id],
    queryFn: () => userOrderStats(id!),
    enabled: !!id && !!profile,
  })

  const { data: companiesData } = useQuery({
    queryKey: ['companies', 'active'],
    queryFn: () => listCompanies({ pageSize: 200, type: 'all' }),
  })

  const mutSave = useMutation({
    mutationFn: async () => {
      if (!profile) return
      const nextPhone = draft.phone ?? profile.phone
      const last = (draft.last_name ?? profile.last_name ?? '').trim()
      const first = (draft.first_name ?? profile.first_name ?? '').trim()
      const middle = (draft.middle_name ?? profile.middle_name ?? '').trim()
      const nextRole: UserRole =
        canEditRole ? ((draft.role as UserRole) ?? profile.role) : profile.role
      const nextCompanyId = draft.company_id ?? profile.company_id
      const regAddress = (draft.registration_address ?? profile.registration_address ?? '').trim()

      // Required-валидация по ТЗ. Кидаем Error → onError выведет в AlertDialog.
      if (!last) throw new Error(t('users.validation.lastName'))
      if (!first) throw new Error(t('users.validation.firstName'))
      if (nextRole === 'inspector' && !middle) {
        throw new Error(t('users.validation.middleName'))
      }
      if (String(nextPhone).replace(/\D/g, '').length < 11) {
        throw new Error(t('users.validation.phone'))
      }
      if (nextRole !== 'dispatcher' && nextRole !== 'admin' && !nextCompanyId) {
        throw new Error(t('users.validation.company'))
      }
      if ((nextRole === 'inspector' || nextRole === 'manager') && !regAddress) {
        throw new Error(
          nextRole === 'inspector'
            ? t('users.validation.registrationAddress')
            : t('users.validation.locationAddress'),
        )
      }
      if (nextRole === 'inspector') {
        const ps = (draft.passport_series ?? profile.passport_series ?? '').trim()
        const pn = (draft.passport_number ?? profile.passport_number ?? '').trim()
        const pby = (draft.passport_issued_by ?? profile.passport_issued_by ?? '').trim()
        const pat = draft.passport_issued_at ?? profile.passport_issued_at
        if (!ps || !pn) throw new Error(t('users.validation.passportSeriesNumber'))
        if (!pby) throw new Error(t('users.validation.passportIssuedBy'))
        if (!pat) throw new Error(t('users.validation.passportIssuedAt'))
        if (!(draft.passport_main_url ?? profile.passport_main_url)) {
          throw new Error(t('users.validation.passportMainScan'))
        }
        if (!(draft.passport_registration_url ?? profile.passport_registration_url)) {
          throw new Error(t('users.validation.passportRegistrationScan'))
        }
      }
      if (nextRole === 'manager') {
        const pn = (draft.power_of_attorney_number ?? profile.power_of_attorney_number ?? '').trim()
        const pe = draft.power_of_attorney_expires_at ?? profile.power_of_attorney_expires_at
        if (!pn) throw new Error(t('users.validation.poaNumber'))
        if (!pe) throw new Error(t('users.validation.poaExpiresAt'))
        if (!(draft.power_of_attorney_url ?? profile.power_of_attorney_url)) {
          throw new Error(t('users.validation.poaScan'))
        }
      }
      // Синхронизируем full_name с last/first/middle, если они задействованы.
      // Если все три пустые — не трогаем full_name (исторические записи).
      const composedFullName = [last, first, middle].filter(Boolean).join(' ').trim()
      const nextFullName =
        composedFullName.length > 0
          ? composedFullName
          : (draft.full_name ?? profile.full_name)
      const payload: Partial<Profile> = {
        full_name: nextFullName,
        last_name: last || null,
        first_name: first || null,
        middle_name: middle || null,
        email: draft.email ?? profile.email,
        company_id: draft.company_id ?? profile.company_id,
        role: canEditRole ? ((draft.role as UserRole) ?? profile.role) : profile.role,
        has_pd_access: draft.has_pd_access ?? profile.has_pd_access,
        access_scope: draft.access_scope ?? profile.access_scope,
        passport_series: draft.passport_series ?? profile.passport_series,
        passport_number: draft.passport_number ?? profile.passport_number,
        passport_issued_by: draft.passport_issued_by ?? profile.passport_issued_by,
        passport_issued_at: draft.passport_issued_at ?? profile.passport_issued_at,
        power_of_attorney_number:
          draft.power_of_attorney_number ?? profile.power_of_attorney_number,
        power_of_attorney_expires_at:
          draft.power_of_attorney_expires_at ?? profile.power_of_attorney_expires_at,
        registration_address: draft.registration_address ?? profile.registration_address,
        passport_main_url: draft.passport_main_url ?? profile.passport_main_url,
        passport_registration_url:
          draft.passport_registration_url ?? profile.passport_registration_url,
        power_of_attorney_url:
          draft.power_of_attorney_url ?? profile.power_of_attorney_url,
      }
      // Сравниваем только цифры, чтобы не зависеть от форматирования (пробелы/дефисы).
      // В RPC передаём нормализованный E.164 (+7XXXXXXXXXX) без пробелов.
      const nextDigits = digitsOnly(nextPhone)
      if (nextDigits !== digitsOnly(profile.phone)) payload.phone = `+${nextDigits}`
      await updateProfile(profile.id, payload)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['profile', id] })
      void qc.invalidateQueries({ queryKey: ['users'] })
      setEditing(false)
      setDraft({})
    },
    onError: (e: Error) => setErrorMessage(e.message),
  })

  const mutDeactivate = useMutation({
    mutationFn: () => deactivateUser(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['profile', id] })
      void qc.invalidateQueries({ queryKey: ['users'] })
    },
    onError: (e: Error) => setErrorMessage(e.message),
  })

  const mutActivate = useMutation({
    mutationFn: () => activateUser(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['profile', id] })
      void qc.invalidateQueries({ queryKey: ['users'] })
    },
    onError: (e: Error) => setErrorMessage(e.message),
  })

  const mutSoftDelete = useMutation({
    mutationFn: () => softDeleteUser(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['profile', id] })
      void qc.invalidateQueries({ queryKey: ['users'] })
    },
    onError: (e: Error) => setErrorMessage(e.message),
  })

  const mutResetPassword = useMutation({
    mutationFn: (newPassword: string) => resetPasswordForUser(id, newPassword),
    onSuccess: () => {
      setResetPwDraft(null)
      setErrorMessage(t('users.passwordResetDone'))
    },
    onError: (e: Error) => setErrorMessage(e.message),
  })

  const p = profile
  const display = (editing && p ? { ...p, ...draft } : p) as Profile
  const currentPhotoUrl = getUserPhotoUrl(display ?? ({} as Profile))
  const photoPreviewUrl = useMemo(
    () => (photoFile ? URL.createObjectURL(photoFile) : currentPhotoUrl),
    [photoFile, currentPhotoUrl],
  )
  useEffect(
    () => () => {
      if (photoFile && photoPreviewUrl) URL.revokeObjectURL(photoPreviewUrl)
    },
    [photoFile, photoPreviewUrl],
  )
  useEffect(() => {
    if (!photoViewerUrl) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPhotoViewerUrl(null)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [photoViewerUrl])

  if (profileLoading && !profile) {
    return (
      <div className="w-full max-w-none px-6 py-4 lg:px-8">
        <DetailHeaderSkeleton />
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
          <SectionSkeleton lines={5} className="lg:col-span-3" />
          <SectionSkeleton lines={3} className="lg:col-span-2" />
          <SectionSkeleton lines={2} className="lg:col-span-5" />
        </div>
      </div>
    )
  }

  if (!profile || !p) {
    return <p className="text-[var(--stryzh-color-text-secondary)]">{t('users.notFound')}</p>
  }
  const companies = companiesData?.items ?? []
  function companyName(id: string | null) {
    if (!id) return t('users.stryzhPlatform')
    return companies.find((c) => c.id === id)?.name ?? id
  }

  // Фильтр ролей по типу выбранной компании (parity с UserDrawer при создании).
  // «Стриж» (без компании) → только диспетчер.
  // Компания-исполнитель → инспектор + руководитель исполнителя.
  // Компания-заказчик   → менеджер + руководитель заказчика.
  // Если у юзера в БД роль не из этого списка (легаси/кривой импорт),
  // показываем все 5, чтобы админ мог исправить вручную.
  const selectedCompany = companies.find((c) => c.id === display.company_id)
  const ALL_ROLE_OPTIONS: { value: UserRole; label: string }[] = [
    { value: 'dispatcher', label: t('role.dispatcher') },
    { value: 'manager', label: t('role.manager') },
    { value: 'inspector', label: t('role.inspector') },
    { value: 'customer_supervisor', label: t('role.customerSupervisor') },
    { value: 'contractor_supervisor', label: t('role.contractorSupervisor') },
  ]
  const validRoleOptions: { value: UserRole; label: string }[] = !selectedCompany
    ? [{ value: 'dispatcher', label: t('role.dispatcher') }]
    : selectedCompany.type === 'contractor'
      ? [
          { value: 'inspector', label: t('role.inspector') },
          { value: 'contractor_supervisor', label: t('role.contractorSupervisor') },
        ]
      : [
          { value: 'manager', label: t('role.manager') },
          { value: 'customer_supervisor', label: t('role.customerSupervisor') },
        ]
  const roleOptions = validRoleOptions.some((o) => o.value === display.role)
    ? validRoleOptions
    : ALL_ROLE_OPTIONS

  function startEdit() {
    setDraft({
      full_name: p!.full_name,
      last_name: p!.last_name,
      first_name: p!.first_name,
      middle_name: p!.middle_name,
      phone: formatPhoneInput('', p!.phone),
      email: p!.email,
      company_id: p!.company_id,
      role: p!.role,
      has_pd_access: p!.has_pd_access,
      passport_series: p!.passport_series,
      passport_number: p!.passport_number,
      passport_issued_by: p!.passport_issued_by,
      passport_issued_at: p!.passport_issued_at,
      power_of_attorney_number: p!.power_of_attorney_number,
      power_of_attorney_expires_at: p!.power_of_attorney_expires_at,
      registration_address: p!.registration_address,
      passport_main_url: p!.passport_main_url,
      passport_registration_url: p!.passport_registration_url,
      power_of_attorney_url: p!.power_of_attorney_url,
    })
    setPhotoFile(null)
    setEditing(true)
  }

  async function onPhotoFileChange(file: File | null) {
    if (!file) {
      setPhotoFile(null)
      return
    }
    setPhotoFile(file)
    try {
      const uploadedUrl = await uploadUserPhoto(p!.id, file)
      setDraft((prev) => ({
        ...prev,
        access_scope: mergeUserPhotoIntoScope(
          (prev.access_scope as Record<string, unknown> | null | undefined) ?? p!.access_scope,
          uploadedUrl,
        ),
      }))
    } catch (e) {
      setErrorMessage(e instanceof Error ? e.message : t('users.photoUploadError'))
    }
  }

  return (
    <div className="w-full max-w-none px-6 py-4 lg:px-8">
      <AlertDialog
        open={errorMessage != null}
        title={t('users.errorTitle')}
        message={errorMessage ?? ''}
        onClose={() => setErrorMessage(null)}
      />
      <ConfirmDialog
        open={confirmDeactivateOpen}
        title={t('users.deactivate.title')}
        cancelLabel={t('common.cancel')}
        confirmLabel={t('users.deactivate.confirm')}
        confirmVariant="danger"
        onCancel={() => setConfirmDeactivateOpen(false)}
        onConfirm={() => {
          setConfirmDeactivateOpen(false)
          mutDeactivate.mutate()
        }}
      >
        {t('users.deactivate.body')}
      </ConfirmDialog>
      <ConfirmDialog
        open={confirmDeleteOpen}
        title={t('users.delete.title')}
        cancelLabel={t('common.cancel')}
        confirmLabel={t('users.delete.confirm')}
        confirmVariant="danger"
        onCancel={() => setConfirmDeleteOpen(false)}
        onConfirm={() => {
          setConfirmDeleteOpen(false)
          mutSoftDelete.mutate()
        }}
      >
        {t('users.delete.body')}
      </ConfirmDialog>
      {resetPwDraft != null ? (
        <PasswordDialog
          password={resetPwDraft}
          title={t('users.resetPassword')}
          isApplying={mutResetPassword.isPending}
          onClose={() => setResetPwDraft(null)}
          onApply={(value) => mutResetPassword.mutateAsync(value)}
        />
      ) : null}
      <Breadcrumbs
        items={[
          { label: t('users.title'), to: '/users' },
          { label: p.full_name },
        ]}
      />
      <header className="mb-6 flex flex-col gap-2">
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
          <div className="flex min-w-0 items-center gap-3">
            {currentPhotoUrl ? (
              <button
                type="button"
                onClick={() => setPhotoViewerUrl(currentPhotoUrl)}
                className="shrink-0 rounded-full transition hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[color:var(--stryzh-color-accent-orange)]"
                aria-label={t('users.openProfilePhoto')}
              >
                <img
                  src={currentPhotoUrl}
                  alt={p.full_name}
                  className="h-14 w-14 rounded-full border border-[color:var(--stryzh-color-border-default)] object-cover"
                />
              </button>
            ) : (
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-fill-input)] text-xl text-[color:var(--stryzh-color-text-secondary)]">
                {p.full_name.charAt(0).toUpperCase()}
              </div>
            )}
            <h1 className="min-w-0 break-words font-[family-name:var(--stryzh-font-family-space-grotesk)] text-[length:var(--stryzh-font-size-hero)] font-semibold tracking-[var(--stryzh-letter-spacing-space-grotesk)] text-[color:var(--stryzh-color-text-primary)]">
              {p.full_name}
            </h1>
          </div>
          <div className="flex min-w-0 max-w-full flex-wrap items-center justify-end gap-2">
            {!editing ? (
              <>
                <Button variant="outline" onClick={startEdit}>
                  {t('users.edit')}
                </Button>
                {p.deleted_at ? null : p.is_active ? (
                  <Button
                    variant="danger"
                    disabled={mutDeactivate.isPending}
                    onClick={() => setConfirmDeactivateOpen(true)}
                  >
                    {t('users.block')}
                  </Button>
                ) : (
                  <Button
                    variant="primary"
                    disabled={mutActivate.isPending}
                    onClick={() => mutActivate.mutate()}
                  >
                    {t('users.unblock')}
                  </Button>
                )}
                {p.deleted_at ? null : (
                  <Button
                    variant="outline"
                    onClick={() => setResetPwDraft(generateProvisionalPassword())}
                  >
                    {t('users.resetPassword')}
                  </Button>
                )}
                {!p.deleted_at ? (
                  <Button
                    variant="danger"
                    disabled={mutSoftDelete.isPending}
                    onClick={() => setConfirmDeleteOpen(true)}
                  >
                    {t('users.delete.action')}
                  </Button>
                ) : null}
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={() => {
                    setEditing(false)
                    setDraft({})
                  }}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  variant="primary"
                  disabled={mutSave.isPending}
                  onClick={() => mutSave.mutate()}
                >
                  {t('common.save')}
                </Button>
              </>
            )}
          </div>
        </div>
        <p className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-text-secondary)]">
          {t('users.createdAt', { date: formatMoscowDateTime(p.created_at) })}
        </p>
      </header>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
        <section className="rounded-xl border border-[var(--stryzh-color-border-default)] bg-[var(--stryzh-color-surface)] p-4 lg:col-span-3">
          <h2 className="stryzh-heading-card mb-4">{t('users.sections.main')}</h2>
          {editing ? (
            <div className="grid gap-3">
              <div className="grid gap-3 max-[639px]:grid-cols-1 min-[640px]:grid-cols-3 min-[640px]:[grid-template-columns:1fr_1fr_1fr]">
                <Input
                  label={t('users.fields.lastName')}
                  required
                  value={display.last_name ?? ''}
                  onChange={(e) =>
                    setDraft({ ...draft, last_name: e.target.value || null })
                  }
                />
                <Input
                  label={t('users.fields.firstName')}
                  required
                  value={display.first_name ?? ''}
                  onChange={(e) =>
                    setDraft({ ...draft, first_name: e.target.value || null })
                  }
                />
                <Input
                  label={t('users.fields.middleName')}
                  required={display.role === 'inspector'}
                  value={display.middle_name ?? ''}
                  onChange={(e) =>
                    setDraft({ ...draft, middle_name: e.target.value || null })
                  }
                />
              </div>
              <div className="grid gap-3 max-[639px]:grid-cols-1 min-[640px]:grid-cols-2 min-[640px]:[grid-template-columns:1fr_1fr]">
                <div className="min-[640px]:border-r min-[640px]:border-solid min-[640px]:border-[rgba(255,255,255,0.08)] min-[640px]:pr-4">
                  <Input
                    label={t('users.fields.fullNameDisplay')}
                    value={display.full_name}
                    onChange={(e) => setDraft({ ...draft, full_name: e.target.value })}
                    hint={t('users.fields.fullNameHint')}
                  />
                </div>
                <div className="min-[640px]:pl-4">
                  <label className="flex flex-col gap-1">
                    <span className="stryzh-input-label">{t('users.fields.role')}</span>
                    {canEditRole ? (
                      <Select
                        value={display.role}
                        onChange={(v) => setDraft({ ...draft, role: v as UserRole })}
                        // admin не назначаем через UI; список фильтруется по типу
                        // выбранной компании (см. roleOptions выше). Legacy-юзеры
                        // с несовместимой парой company/role видят все 5 ролей,
                        // чтобы админ мог исправить.
                        options={roleOptions}
                      />
                    ) : (
                      <div className="rounded-[var(--stryzh-radius-md)] border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-fill-input)] px-3 py-2 text-sm text-[color:var(--stryzh-color-text-primary)]">
                        {roleLabel(display.role)}
                      </div>
                    )}
                  </label>
                </div>
                <div className="min-[640px]:border-r min-[640px]:border-solid min-[640px]:border-[rgba(255,255,255,0.08)] min-[640px]:pr-4">
                  <Input
                    label={t('users.fields.phone')}
                    required
                    value={display.phone}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        phone: formatPhoneInput(display.phone, e.target.value),
                      })
                    }
                  />
                </div>
                <div className="min-[640px]:pl-4">
                  <Input
                    label={t('users.fields.email')}
                    value={display.email ?? ''}
                    onChange={(e) => setDraft({ ...draft, email: e.target.value || null })}
                  />
                </div>
              </div>
              {display.role === 'manager' ? (
                <div className="grid gap-3 rounded-[var(--stryzh-radius-md)] border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-fill-input)] p-3 max-[639px]:grid-cols-1 min-[640px]:grid-cols-2 min-[640px]:[grid-template-columns:1fr_1fr]">
                  <Input
                    label={t('users.fields.poaNumber')}
                    required
                    value={display.power_of_attorney_number ?? ''}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        power_of_attorney_number: e.target.value || null,
                      })
                    }
                  />
                  <Input
                    label={t('users.fields.poaExpiresAt')}
                    required
                    type="date"
                    value={display.power_of_attorney_expires_at ?? ''}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        power_of_attorney_expires_at: e.target.value || null,
                      })
                    }
                  />
                  <p className="text-xs text-[var(--stryzh-color-text-secondary)] min-[640px]:col-span-2">
                    {t('users.fields.poaScanHint')}
                  </p>
                </div>
              ) : null}
              <label className="flex flex-col gap-1">
                <span className="stryzh-input-label !inline-flex items-center gap-3 leading-none">
                  {display.role !== 'dispatcher' ? (
                    <span
                      aria-hidden
                      title={t('users.requiredField')}
                      className="inline-block h-1.5 w-1.5 rounded-full bg-[color:var(--stryzh-color-accent-orange)]"
                    />
                  ) : null}
                  <span>{t('users.fields.company')}</span>
                </span>
                <Select
                  value={display.company_id ?? ''}
                  onChange={(v) => setDraft({ ...draft, company_id: v || null })}
                  options={companies.map((c) => ({ value: c.id, label: c.name }))}
                  emptyLabel={t('users.stryzhPlatform')}
                />
              </label>
              <PillToggle
                checked={display.has_pd_access}
                onChange={(v) => setDraft({ ...draft, has_pd_access: v })}
                label={t('users.fields.pdAccess')}
              />
              <label className="flex flex-col gap-1">
                <span className="stryzh-input-label">{t('users.fields.photo')}</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    void onPhotoFileChange(e.target.files?.[0] ?? null)
                  }}
                  className="rounded-[var(--stryzh-radius-md)] border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-fill-input)] px-3 py-2 text-sm text-[color:var(--stryzh-color-text-primary)]"
                />
                {photoPreviewUrl ? (
                  <button
                    type="button"
                    onClick={() => setPhotoViewerUrl(photoPreviewUrl)}
                    className="mt-2 w-fit rounded-full transition hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[color:var(--stryzh-color-accent-orange)]"
                    aria-label={t('users.openPhotoPreview')}
                  >
                    <img
                      src={photoPreviewUrl}
                      alt={t('users.fields.photoPreviewAlt')}
                      className="h-20 w-20 rounded-full border border-[color:var(--stryzh-color-border-default)] object-cover"
                    />
                  </button>
                ) : null}
                {currentPhotoUrl ? (
                  <Button
                    variant="outline"
                    onClick={() =>
                      setDraft({
                        ...draft,
                        access_scope: mergeUserPhotoIntoScope(display.access_scope, null),
                      })}
                  >
                    {t('users.deletePhoto')}
                  </Button>
                ) : null}
              </label>
            </div>
          ) : (
            <>
              <dl className="grid gap-4 max-[639px]:grid-cols-1 min-[640px]:grid-cols-2 min-[640px]:[grid-template-columns:1fr_1fr]">
                <div className="min-w-0 min-[640px]:border-r min-[640px]:border-solid min-[640px]:border-[rgba(255,255,255,0.08)] min-[640px]:pr-4">
                  <dt className="stryzh-input-label">{t('users.fields.fullName')}</dt>
                  <dd className={valueClassName}>{p.full_name}</dd>
                </div>
                <div className="min-w-0 min-[640px]:pl-4">
                  <dt className="stryzh-input-label">{t('users.fields.role')}</dt>
                  <dd className={valueClassName}>{roleLabel(p.role)}</dd>
                </div>
                <div className="min-w-0 min-[640px]:border-r min-[640px]:border-solid min-[640px]:border-[rgba(255,255,255,0.08)] min-[640px]:pr-4">
                  <dt className="stryzh-input-label">{t('users.fields.phone')}</dt>
                  <dd className={valueClassName}>{formatPhoneDisplay(p.phone)}</dd>
                </div>
                <div className="min-w-0 min-[640px]:pl-4">
                  <dt className="stryzh-input-label">{t('users.fields.email')}</dt>
                  <dd className={valueClassName}>{p.email ?? '—'}</dd>
                </div>
                <div className="min-w-0 min-[640px]:border-r min-[640px]:border-solid min-[640px]:border-[rgba(255,255,255,0.08)] min-[640px]:pr-4">
                  <dt className="stryzh-input-label">{t('users.fields.company')}</dt>
                  <dd className={valueClassName}>
                    {p.company_id ? (
                      <Link
                        className="text-[color:var(--stryzh-color-accent-orange)] hover:underline"
                        to={`/companies/${p.company_id}`}
                      >
                        {companyName(p.company_id)}
                      </Link>
                    ) : (
                      '—'
                    )}
                  </dd>
                </div>
                <div className="hidden min-w-0 min-[640px]:block min-[640px]:pl-4">
                  <dt className="stryzh-input-label invisible" aria-hidden>
                    {t('users.fields.company')}
                  </dt>
                  <dd className={valueClassName}>—</dd>
                </div>
              </dl>
              <div className="mt-4 border-t border-solid border-[rgba(255,255,255,0.08)] pt-4">
                <dl className="grid gap-4 max-[639px]:grid-cols-1 min-[640px]:grid-cols-2 min-[640px]:[grid-template-columns:1fr_1fr]">
                  <div className="min-w-0 min-[640px]:border-r min-[640px]:border-solid min-[640px]:border-[rgba(255,255,255,0.08)] min-[640px]:pr-4">
                    <dt className="stryzh-input-label">{t('users.fields.pd')}</dt>
                    <dd className={valueClassName}>{p.has_pd_access ? t('users.yes') : t('users.no')}</dd>
                  </div>
                  <div className="hidden min-w-0 min-[640px]:block min-[640px]:pl-4">
                    <dt className="stryzh-input-label invisible" aria-hidden>
                      {t('users.fields.pd')}
                    </dt>
                    <dd className={valueClassName}>—</dd>
                  </div>
                  <div className="min-w-0 min-[640px]:border-r min-[640px]:border-solid min-[640px]:border-[rgba(255,255,255,0.08)] min-[640px]:pr-4">
                    <dt className="stryzh-input-label">{t('users.fields.createdAt')}</dt>
                    <dd className={valueClassName}>{formatMoscowDateTime(p.created_at)}</dd>
                  </div>
                  <div className="min-w-0 min-[640px]:pl-4">
                    <dt className="stryzh-input-label">{t('users.fields.lastSignIn')}</dt>
                    <dd className={valueClassName}>
                      {p.last_sign_in_at
                        ? formatMoscowDateTime(p.last_sign_in_at)
                        : '—'}
                    </dd>
                  </div>
                </dl>
              </div>
            </>
          )}
        </section>

        <section className="rounded-xl border border-[var(--stryzh-color-border-default)] bg-[var(--stryzh-color-surface)] p-4 lg:col-span-2">
          <h2 className="stryzh-heading-card mb-4">{t('users.sections.device')}</h2>
          {debug ? (
            <dl className="grid gap-2 text-sm">
              <div>
                <dt className="stryzh-input-label">{t('users.device.platform')}</dt>
                <dd className={valueClassName}>{debug.platform || '—'}</dd>
              </div>
              <div>
                <dt className="stryzh-input-label">{t('users.device.appVersion')}</dt>
                <dd className={valueClassName}>{debug.app_version || '—'}</dd>
              </div>
              <div>
                <dt className="stryzh-input-label">{t('users.device.lastEvent')}</dt>
                <dd className={valueClassName}>
                  {formatMoscowDateTime(debug.created_at)} · {debug.kind}/{debug.name}
                </dd>
              </div>
              {debug.payload ? (
                <div>
                  <dt className="stryzh-input-label">{t('users.device.payload')}</dt>
                  <pre className="mt-1 max-h-[var(--stryzh-layout-scroll-area-max-height)] overflow-auto rounded-[var(--stryzh-radius-md)] bg-[color:var(--stryzh-color-fill-input)] p-3 font-[family-name:var(--stryzh-font-family-mono)] text-[length:var(--stryzh-font-size-xs)]">
                    {JSON.stringify(debug.payload, null, 2)}
                  </pre>
                </div>
              ) : null}
            </dl>
          ) : (
            <p className="text-sm text-[var(--stryzh-color-text-secondary)]">{t('common.noData')}</p>
          )}
        </section>

        {authDebug ? (
          <section className="rounded-xl border border-[var(--stryzh-color-border-default)] bg-[var(--stryzh-color-surface)] p-4 lg:col-span-5">
            <h2 className="stryzh-heading-card mb-4">{t('users.sections.auth')}</h2>
            <dl className="grid gap-4 max-[639px]:grid-cols-1 min-[640px]:grid-cols-3">
              <div className="min-w-0">
                <dt className="stryzh-input-label">auth.users.email</dt>
                <dd className={`${valueClassName} font-[family-name:var(--stryzh-font-family-mono)] break-all`}>
                  {authDebug.auth_user?.email ?? '—'}
                </dd>
              </div>
              <div className="min-w-0">
                <dt className="stryzh-input-label">auth.users.last_sign_in_at</dt>
                <dd className={valueClassName}>
                  {authDebug.auth_user?.last_sign_in_at
                    ? formatMoscowDateTime(authDebug.auth_user.last_sign_in_at)
                    : '—'}
                </dd>
              </div>
              <div className="min-w-0">
                <dt className="stryzh-input-label">auth.users.email_confirmed_at</dt>
                <dd className={valueClassName}>
                  {authDebug.auth_user?.email_confirmed_at
                    ? formatMoscowDateTime(authDebug.auth_user.email_confirmed_at)
                    : '—'}
                </dd>
              </div>
              <div className="min-w-0">
                <dt className="stryzh-input-label">banned_until</dt>
                <dd className={valueClassName}>
                  {authDebug.auth_user?.banned_until
                    ? formatMoscowDateTime(authDebug.auth_user.banned_until)
                    : '—'}
                </dd>
              </div>
              <div className="min-w-0">
                <dt className="stryzh-input-label">deleted_at</dt>
                <dd className={valueClassName}>
                  {authDebug.auth_user?.deleted_at
                    ? formatMoscowDateTime(authDebug.auth_user.deleted_at)
                    : '—'}
                </dd>
              </div>
              <div className="min-w-0">
                <dt className="stryzh-input-label">{t('users.auth.userExists')}</dt>
                <dd className={valueClassName}>{authDebug.auth_user ? t('users.yes') : t('users.auth.lost')}</dd>
              </div>
            </dl>
            <div className="mt-4">
              <h3 className="stryzh-input-label mb-2">
                auth.identities ({authDebug.identities.length})
              </h3>
              {authDebug.identities.length === 0 ? (
                <p className="text-sm text-[var(--stryzh-color-text-secondary)]">
                  {t('users.auth.noIdentities')}
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr className="text-[var(--stryzh-color-text-secondary)]">
                        <th className="py-2 pr-3">provider</th>
                        <th className="py-2 pr-3">provider_id</th>
                        <th className="py-2 pr-3">identity_data.email</th>
                        <th className="py-2 pr-3">created_at</th>
                        <th className="py-2">updated_at</th>
                      </tr>
                    </thead>
                    <tbody>
                      {authDebug.identities.map((i) => {
                        const expectedEmail = authDebug.auth_user?.email
                        const stale =
                          expectedEmail != null &&
                          i.provider === 'email' &&
                          i.provider_id.includes('@') &&
                          i.provider_id !== expectedEmail
                        return (
                          <tr
                            key={i.id}
                            className="border-t border-[var(--stryzh-color-border-default)]"
                          >
                            <td className="py-2 pr-3">{i.provider}</td>
                            <td
                              className={`py-2 pr-3 font-[family-name:var(--stryzh-font-family-mono)] break-all ${
                                stale ? 'text-[color:var(--stryzh-color-accent-orange)]' : ''
                              }`}
                              title={stale ? t('users.auth.staleIdentity') : undefined}
                            >
                              {i.provider_id}
                              {stale ? ' ⚠' : ''}
                            </td>
                            <td className="py-2 pr-3 font-[family-name:var(--stryzh-font-family-mono)] break-all">
                              {i.identity_email ?? '—'}
                            </td>
                            <td className="py-2 pr-3 whitespace-nowrap">
                              {formatMoscowDateTime(i.created_at)}
                            </td>
                            <td className="py-2 whitespace-nowrap">
                              {formatMoscowDateTime(i.updated_at)}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </section>
        ) : null}

        {display.role === 'inspector' || display.role === 'manager' ? (
          <>
            <section className="rounded-xl border border-[var(--stryzh-color-border-default)] bg-[var(--stryzh-color-surface)] p-4 lg:col-span-5">
              <h2 className="stryzh-heading-card mb-4">{t('users.sections.addressDetails')}</h2>
              {editing ? (
                <div className="grid gap-3">
                  <Input
                    label={
                      display.role === 'inspector'
                        ? t('users.fields.registrationAddress')
                        : t('users.fields.locationAddress')
                    }
                    required
                    value={display.registration_address ?? ''}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        registration_address: e.target.value || null,
                      })
                    }
                  />
                  {display.role === 'inspector' ? (
                    <div className="grid gap-3 max-[639px]:grid-cols-1 min-[640px]:grid-cols-2 min-[640px]:[grid-template-columns:1fr_1fr]">
                      <Input
                        label={t('users.fields.passportSeries')}
                        required
                        inputMode="numeric"
                        maxLength={4}
                        value={display.passport_series ?? ''}
                        onChange={(e) =>
                          setDraft({
                            ...draft,
                            passport_series: e.target.value || null,
                          })
                        }
                      />
                      <Input
                        label={t('users.fields.passportNumber')}
                        required
                        inputMode="numeric"
                        maxLength={6}
                        value={display.passport_number ?? ''}
                        onChange={(e) =>
                          setDraft({
                            ...draft,
                            passport_number: e.target.value || null,
                          })
                        }
                      />
                      <Input
                        label={t('users.fields.passportIssuedBy')}
                        required
                        value={display.passport_issued_by ?? ''}
                        onChange={(e) =>
                          setDraft({
                            ...draft,
                            passport_issued_by: e.target.value || null,
                          })
                        }
                      />
                      <Input
                        label={t('users.fields.passportIssuedAt')}
                        required
                        type="date"
                        value={display.passport_issued_at ?? ''}
                        onChange={(e) =>
                          setDraft({
                            ...draft,
                            passport_issued_at: e.target.value || null,
                          })
                        }
                      />
                    </div>
                  ) : null}
                </div>
              ) : (
                <dl className="grid gap-4 max-[639px]:grid-cols-1 min-[640px]:grid-cols-2 min-[640px]:[grid-template-columns:1fr_1fr]">
                  <div className="min-w-0 min-[640px]:border-r min-[640px]:border-solid min-[640px]:border-[rgba(255,255,255,0.08)] min-[640px]:pr-4">
                    <dt className="stryzh-input-label">
                      {p.role === 'inspector'
                        ? t('users.fields.registrationAddress')
                        : t('users.fields.locationAddress')}
                    </dt>
                    <dd className={valueClassName}>{p.registration_address ?? '—'}</dd>
                  </div>
                  {p.role === 'inspector' ? (
                    <>
                      <div className="min-w-0 min-[640px]:pl-4">
                        <dt className="stryzh-input-label">{t('users.fields.passport')}</dt>
                        <dd className={valueClassName}>
                          {p.passport_series && p.passport_number
                            ? `${p.passport_series} ${p.passport_number}`
                            : '—'}
                        </dd>
                      </div>
                      <div className="min-w-0 min-[640px]:border-r min-[640px]:border-solid min-[640px]:border-[rgba(255,255,255,0.08)] min-[640px]:pr-4">
                        <dt className="stryzh-input-label">{t('users.fields.passportIssuedBy')}</dt>
                        <dd className={valueClassName}>{p.passport_issued_by ?? '—'}</dd>
                      </div>
                      <div className="min-w-0 min-[640px]:pl-4">
                        <dt className="stryzh-input-label">{t('users.fields.passportIssuedAt')}</dt>
                        <dd className={valueClassName}>{p.passport_issued_at ?? '—'}</dd>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="min-w-0 min-[640px]:pl-4">
                        <dt className="stryzh-input-label">{t('users.fields.poaNumber')}</dt>
                        <dd className={valueClassName}>
                          {p.power_of_attorney_number ?? '—'}
                        </dd>
                      </div>
                      <div className="min-w-0 min-[640px]:border-r min-[640px]:border-solid min-[640px]:border-[rgba(255,255,255,0.08)] min-[640px]:pr-4">
                        <dt className="stryzh-input-label">{t('users.fields.poaValidUntil')}</dt>
                        <dd className={valueClassName}>
                          {p.power_of_attorney_expires_at ?? '—'}
                        </dd>
                      </div>
                    </>
                  )}
                </dl>
              )}
            </section>

            <section className="rounded-xl border border-[var(--stryzh-color-border-default)] bg-[var(--stryzh-color-surface)] p-4 lg:col-span-5">
              <h2 className="stryzh-heading-card mb-4">{t('users.sections.documents')}</h2>
              <div className="grid gap-3 max-[639px]:grid-cols-1 min-[640px]:grid-cols-3">
                {display.role === 'inspector' ? (
                  <>
                    <DocumentUploader
                      label={t('users.documents.passportMain')}
                      currentPath={display.passport_main_url ?? null}
                      editing={editing}
                      getSignedUrl={getUserDocumentSignedUrl}
                      onUpload={(file) =>
                        uploadUserDocument(p.id, 'passport-main', file)
                      }
                      onPathChange={(path) =>
                        setDraft((prev) => ({ ...prev, passport_main_url: path }))
                      }
                    />
                    <DocumentUploader
                      label={t('users.documents.passportRegistration')}
                      currentPath={display.passport_registration_url ?? null}
                      editing={editing}
                      getSignedUrl={getUserDocumentSignedUrl}
                      onUpload={(file) =>
                        uploadUserDocument(p.id, 'passport-registration', file)
                      }
                      onPathChange={(path) =>
                        setDraft((prev) => ({
                          ...prev,
                          passport_registration_url: path,
                        }))
                      }
                    />
                  </>
                ) : (
                  <DocumentUploader
                    label={t('users.documents.poa')}
                    currentPath={display.power_of_attorney_url ?? null}
                    editing={editing}
                    getSignedUrl={getUserDocumentSignedUrl}
                    onUpload={(file) =>
                      uploadUserDocument(p.id, 'power-of-attorney', file)
                    }
                    onPathChange={(path) =>
                      setDraft((prev) => ({ ...prev, power_of_attorney_url: path }))
                    }
                  />
                )}
              </div>
            </section>
          </>
        ) : null}

        {stats ? (
          <section className="rounded-xl border border-[var(--stryzh-color-border-default)] bg-[var(--stryzh-color-surface)] p-4 lg:col-span-5">
            <h2 className="stryzh-heading-card mb-4">{t('users.sections.stats')}</h2>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-5 text-sm">
              <div>
                <div className="text-[var(--stryzh-color-text-secondary)]">{t('users.stats.total')}</div>
                <div className="text-xl font-semibold">{stats.total}</div>
              </div>
              <div>
                <div className="text-[var(--stryzh-color-text-secondary)]">{t('users.stats.active')}</div>
                <div className="text-xl font-semibold">{stats.active}</div>
              </div>
              <div>
                <div className="text-[var(--stryzh-color-text-secondary)]">
                  {statusLabelRu('COMPLETED' as OrderStatus)}
                </div>
                <div className="text-xl font-semibold">{stats.completed}</div>
              </div>
              <div>
                <div className="text-[var(--stryzh-color-text-secondary)]">
                  {statusLabelRu('CANCELLED' as OrderStatus)}
                </div>
                <div className="text-xl font-semibold">{stats.cancelled}</div>
              </div>
              <div>
                <div className="text-[var(--stryzh-color-text-secondary)]">{t('users.stats.refused')}</div>
                <div className="text-xl font-semibold">{stats.refused}</div>
              </div>
            </div>
          </section>
        ) : null}

        <section className="rounded-xl border border-[var(--stryzh-color-border-default)] bg-[var(--stryzh-color-surface)] p-4 lg:col-span-5">
          <h2 className="stryzh-heading-card mb-4">{t('users.sections.orders')}</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="text-[var(--stryzh-color-text-secondary)]">
                  <th className="py-2">{t('users.orders.car')}</th>
                  <th className="py-2">{t('users.orders.status')}</th>
                </tr>
              </thead>
              <tbody>
                {(orders ?? []).length === 0 ? (
                  <tr>
                    <td colSpan={2} className={emptyTableCellClassName}>
                      {t('common.noData')}
                    </td>
                  </tr>
                ) : (
                  (orders ?? []).map((o) => (
                    <tr
                      key={o.id}
                      className="cursor-pointer border-t border-[var(--stryzh-color-border-default)] hover:bg-[color:var(--stryzh-color-border-subtle)]"
                      onClick={() => nav(`/orders/${o.id}`)}
                    >
                      <td className="py-2">
                        {o.brand} {o.model}
                      </td>
                      <td className="py-2">{statusLabelRu(o.status as OrderStatus)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
      {photoViewerUrl ? (
        <div
          role="presentation"
          className="fixed inset-0 z-[60] flex items-center justify-center bg-[rgba(0,0,0,0.75)] p-4"
          onClick={() => setPhotoViewerUrl(null)}
        >
          <div
            role="dialog"
            aria-label={t('users.viewProfilePhoto')}
            className="relative"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={photoViewerUrl}
              alt={t('users.profilePhotoAlt')}
              className="h-[50vh] w-[50vh] max-h-[80vh] max-w-[90vw] rounded-full border border-[color:var(--stryzh-color-border-default)] object-cover shadow-[var(--stryzh-shadow-glow-strong)]"
            />
          </div>
        </div>
      ) : null}
    </div>
  )
}
